import { useState, useEffect } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem, CommandGroup, CommandSeparator } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { FolderOpen, Database, ChevronDown, X, Filter } from 'lucide-react';
import api from '@/lib/api';

export default function SchemaGroupFilter({ value, onChange, className }) {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [schemas, setSchemas] = useState([]);

  useEffect(() => {
    Promise.all([api.get('/schema-groups'), api.get('/schemas')])
      .then(([gRes, sRes]) => { setGroups(gRes.data); setSchemas(sRes.data); })
      .catch(() => {});
  }, []);

  // value = { type: 'group'|'schema', id: string, label: string } | null
  const handleSelect = (type, id, label) => {
    onChange({ type, id, label });
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className={`justify-between rounded-none text-left font-normal h-8 text-[11px] ${className || 'w-56'}`} data-testid="schema-group-filter">
          {value ? (
            <span className="flex items-center gap-1.5 truncate">
              {value.type === 'group' ? <FolderOpen className="w-3 h-3 text-[#002FA7] shrink-0" /> : <Database className="w-3 h-3 text-purple-600 shrink-0" />}
              <span className="truncate">{value.label}</span>
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-[#9CA3AF]"><Filter className="w-3 h-3" /> Filter by group/schema</span>
          )}
          <div className="flex items-center gap-0.5 shrink-0 ml-1">
            {value && <X className="w-3 h-3 text-[#9CA3AF] hover:text-red-500" onClick={handleClear} />}
            <ChevronDown className="w-3 h-3 text-[#9CA3AF]" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 rounded-none w-72" align="start">
        <Command className="rounded-none">
          <CommandInput placeholder="Search groups & schemas..." className="h-8 text-xs rounded-none" />
          <CommandList className="max-h-64">
            <CommandEmpty className="py-3 text-xs text-center text-[#9CA3AF]">No results</CommandEmpty>
            <CommandItem value="__all__" onSelect={() => { onChange(null); setOpen(false); }} className="text-xs py-1.5 rounded-none cursor-pointer">
              <Filter className="w-3 h-3 mr-1.5 text-[#9CA3AF]" /> All (no filter)
            </CommandItem>
            <CommandSeparator />
            <CommandGroup heading="Schema Groups">
              {groups.map(g => (
                <CommandItem key={`g-${g.group_id}`} value={`group ${g.name}`} onSelect={() => handleSelect('group', g.group_id, g.name)} className="py-1.5 rounded-none cursor-pointer">
                  <div className="flex items-center gap-2 w-full">
                    <FolderOpen className="w-3 h-3 text-[#002FA7] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-[#0A0A0A]">{g.name}</span>
                      <span className="text-[10px] text-[#9CA3AF] ml-1.5">{g.member_count} schemas</span>
                      {g.gating_field_count > 0 && <span className="text-[10px] text-blue-600 ml-1">{g.gating_field_count} gating</span>}
                    </div>
                    {value?.type === 'group' && value?.id === g.group_id && <span className="text-[10px] text-[#002FA7] shrink-0">Active</span>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Schemas">
              {schemas.map(s => (
                <CommandItem key={`s-${s.schema_id}`} value={`schema ${s.name} ${s.code}`} onSelect={() => handleSelect('schema', s.schema_id, s.name)} className="py-1.5 rounded-none cursor-pointer">
                  <div className="flex items-center gap-2 w-full">
                    <Database className="w-3 h-3 text-purple-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-[#0A0A0A] truncate block">{s.name}</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-[#9CA3AF]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{s.code}</span>
                        <span className="text-[10px] text-[#9CA3AF]">{s.attribute_count} attrs</span>
                        {(s.tags || []).map(t => <span key={t} className="text-[9px] px-1 bg-purple-50 text-purple-600">{t}</span>)}
                      </div>
                    </div>
                    {value?.type === 'schema' && value?.id === s.schema_id && <span className="text-[10px] text-[#002FA7] shrink-0">Active</span>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
